<script>
import {defineComponent} from 'vue'
import Breadcrumbs from '@js/components/Breadcrumbs/Breadcrumbs.vue'
import MobileTopBar from '@js/components/TopBar/MobileTopBar.vue'
import {contacts, requisites} from '@js/company_details.ts'
import {formatPhone} from "@js/helpers.ts";
import {Link} from '@inertiajs/vue3'

export default defineComponent({
    name: 'ReturnGuaranteePage',
    components: {Link, MobileTopBar, Breadcrumbs},
    setup() {
        return {contacts, requisites, formatPhone}
    }
})
</script>

<template>
    <MobileTopBar/>
    <div class="container">
        <Breadcrumbs class="mb-2"/>
        <h1 class="text-2xl md:text-3xl">Обмен и возврат товара — ООО «Дорожные плиты»</h1>
        <p class="text-sm text-gray-600 mt-2">Действует для сайта dorozhnyeplity.ru</p>
        <hr class="mt-5 mb-5"/>

        <!-- 0. Кому пригодится страница -->
        <div class="mb-8 prose">
            <p class="text-sm">
                На этой странице собраны правила возврата и гарантий для покупателей <span class="font-semibold">ООО «Дорожные плиты»</span>.
                Условия действуют для заказов на сайте <span class="font-semibold">https://dorozhnyeplity.ru</span>.
                Для корпоративных клиентов (B2B) приоритет имеют условия договора/счёта-оферты.
            </p>
        </div>

        <!-- 1. Правовая основа -->
        <div class="mb-10">
            <h2 id="returnPolicy" class="text-xl mb-5 font-medium">1. Правовая основа и область применения</h2>
            <p class="text-sm mb-2">
                1.1. Возвраты осуществляются по нормам ГК РФ и Закона РФ «О защите прав потребителей». Для дистанционных продаж физическим лицам действует право возврата товара надлежащего качества
                в течение <span class="font-semibold">7 календарных дней</span> с момента получения.
            </p>
            <p class="text-sm mb-2">
                1.2. Для юридических лиц и индивидуальных предпринимателей порядок возврата регулируется договором поставки и спецификацией.
            </p>
            <p class="text-sm mb-2">
                1.3. Под «Товаром» понимаются железобетонные изделия (ЖБИ) и сопутствующая продукция: дорожные плиты (ПДН/ПАГ), плиты перекрытий (ПК/ПБ), блоки ФБС, бортовые камни (БР), лотки ЛК/Л, кольца КС и доборные элементы, перемычки ПБ/ПП, сваи СВ, лестничные марши/площадки и др., а также бетонные и растворные смеси марок М100–М400.
            </p>
        </div>

        <!-- 2. Товар надлежащего качества -->
        <div class="mb-10">
            <h2 class="text-xl mb-5 font-medium">2. Возврат товара надлежащего качества</h2>
            <p class="text-sm mb-2">2.1. Возможен в течение 7 дней при соблюдении условий:</p>
            <ul class="text-sm list-disc pl-5 mb-3">
                <li>товар не использовался и не подвергался доработке/монтажу;</li>
                <li>сохранены товарный вид, обвязка/упаковка, маркировка и ярлыки;</li>
                <li>есть документ, подтверждающий покупку (счёт/накладная/кассовый чек/УПД);</li>
                <li>товар не относится к индивидуально изготовленным/обработанным позициям.</li>
            </ul>
            <p class="text-sm mb-2">2.2. Возврат осуществляется за счёт Покупателя на склад Продавца после согласования с менеджером.</p>
        </div>

        <!-- 3. Товар с недостатками -->
        <div class="mb-10">
            <h2 class="text-xl mb-5 font-medium">3. Возврат/обмен товара с недостатками</h2>
            <p class="text-sm mb-2">
                3.1. При подтверждённом производственном дефекте или несоответствии спецификации возможны: обмен, соразмерное уменьшение цены, устранение недостатков либо возврат денежных средств.
            </p>
            <p class="text-sm mb-2">
                3.2. Для рассмотрения предоставьте акт/фотофиксацию дефекта, номер заказа и документы по отгрузке. Доставка дефектного товара продавцу или выезд на осмотр согласуются индивидуально. При подтверждении дефекта транспортные расходы несёт Продавец.
            </p>
        </div>

        <!-- 4. Индивидуальные работы -->
        <div class="mb-10">
            <h2 class="text-xl mb-5 font-medium">4. Индивидуальные работы и услуги</h2>
            <p class="text-sm mb-2">
                Позиции, прошедшие обработку по заявке Покупателя (резка в размер, сверление, сварка, гибка, окраска/грунт), сборка/вязка каркасов, а также изделия, изготовленные по индивидуальным параметрам, <span class="font-semibold">не подлежат возврату</span> надлежащего качества.
            </p>
        </div>

        <!-- 5. Процедура и сроки -->
        <div class="mb-10">
            <h2 class="text-xl mb-5 font-medium">5. Как оформить возврат</h2>
            <ol class="text-sm list-decimal pl-5 mb-3">
                <li>Свяжитесь с нами: {{ formatPhone(contacts.sales_department_phone) }} или {{ contacts.info_mail }}.</li>
                <li>Заполните заявление на возврат (по запросу предоставим шаблон) и подготовьте документы по покупке.</li>
                <li>Согласуйте с менеджером способ и время поставки товара на склад.</li>
                <li>Передайте товар и документы на приёмке; получите акт приёма.</li>
            </ol>
            <p class="text-sm mb-2">Возврат денежных средств производится тем же способом, что и оплата, в срок до <span class="font-semibold">10 календарных дней</span> после получения товара и заявления, если иное не предусмотрено договором.</p>
        </div>

        <!-- 6. Доставка и расходы -->
        <div class="mb-10">
            <h2 class="text-xl mb-5 font-medium">6. Доставка и распределение расходов</h2>
            <p class="text-sm mb-2">При возврате товара надлежащего качества транспортные услуги, хранение и иные фактические расходы оплачивает Покупатель. При подтверждённом производственном дефекте расходы несёт Продавец.</p>
            <p class="text-sm mb-2">
                Подробные условия доставки и разгрузки см. на странице
                <Link :href="route('deliveryAndShipping')" class="underline hover:text-navHoverColor font-semibold">«Доставка и отгрузка»</Link>.
            </p>
        </div>

        <!-- 7. Приёмка и документы -->
        <div class="mb-10">
            <h2 class="text-xl mb-5 font-medium">7. Приёмка товара и необходимые документы</h2>
            <ul class="text-sm list-disc pl-5 mb-3">
                <li>накладная/УПД и счёт/КП;</li>
                <li>фото товара и упаковки (при возврате по качеству);</li>
                <li>акт несоответствия (при наличии), отметка перевозчика о внешних повреждениях;</li>
                <li>для юрлиц — доверенность/печать; для физлиц — документ, удостоверяющий личность.</li>
            </ul>
            <p class="text-sm mb-2">Приёмка по количеству/ассортименту производится при отгрузке/получении; расхождения фиксируются актом.</p>
        </div>

        <!-- 8. Возврат через ТК -->
        <div class="mb-10">
            <h2 class="text-xl mb-5 font-medium">8. Возврат через транспортную компанию</h2>
            <p class="text-sm mb-2">Отправка возврата ТК возможна после согласования. Упаковка должна исключать деформации и повреждения кромок/поверхности. Риски случайной порчи в пути до момента приёмки несёт отправитель, если иное не согласовано письменно.</p>
        </div>

        <!-- 9. Гарантия -->
        <div class="mb-10">
            <h2 class="text-xl mb-5 font-medium">9. Гарантийные обязательства</h2>
            <p class="text-sm mb-2">На продукцию распространяются гарантийные обязательства производителя при наличии паспорта качества/сертификата. Гарантия не покрывает повреждения, возникшие вследствие нарушения условий хранения/транспортировки и монтажа, а также естественные следы консервации и допустимые по ГОСТ/ТУ отклонения поверхности.</p>
        </div>

        <!-- 10. Претензионный порядок -->
        <div class="mb-10">
            <h2 class="text-xl mb-5 font-medium">10. Претензионный порядок</h2>
            <p class="text-sm mb-2">Претензию можно направить на {{ contacts.info_mail }} с темой «Претензия по заказу №…». Срок рассмотрения — до 10 рабочих дней. Мы всегда открыты к конструктивному решению вопроса.</p>
        </div>

        <!-- 11. Контакты -->
        <div class="mb-6">
            <h2 class="text-xl mb-5 font-medium">11. Контакты</h2>
            <p class="text-sm mb-2">Тел.: {{ formatPhone(contacts.sales_department_phone) }} • E-mail: {{ contacts.info_mail }}</p>
            <p class="text-sm mb-2">
                ООО «Дорожные плиты», ИНН {{ requisites.inn }}, КПП {{ requisites.kpp }}, ОГРН {{ requisites.ogrn }}<br>
                Юр. адрес: {{ requisites.ur_address }}
            </p>
            <p class="text-sm">
                Также см. <Link :href="route('publicOffer')" class="underline hover:text-navHoverColor font-semibold">Публичную оферту</Link>
                и <Link :href="route('deliveryAndShipping')" class="underline hover:text-navHoverColor font-semibold">Доставку и отгрузку</Link>.
            </p>
        </div>
    </div>
</template>

<style scoped lang="scss">
.prose a {
    @apply text-yellow-600 hover:text-yellow-800 underline;
}
</style>
